import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import MarkFeeModal from './MarkFeeModal';
import LumpSumFeeModal from './LumpSumFeeModal';
import DeleteFeeModal from './DeleteFeeModal';
import FeeReceiptGenerator from './FeeReceiptGenerator';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import useClassesStore from '../../../stateManagers/store/useClassesStore';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FeeRecordItem {
  fee_type_id?: string | { $oid: string };
  fee_name: string;
  amount: number;
  is_optional: boolean;
  is_exempted: boolean;
}

interface FeeRecord {
  student_id: number | string;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | string | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string | number | null;
  paid_amount: string | number | null;
  fees_status: 'paid' | 'unpaid' | 'partial' | 'partially_paid' | 'exempted';
  pay_mode: string | null;
  profile_picture_url?: string | null;
  // ── Full breakdown (for the receipt / print service) ──
  is_exempted?: boolean;
  exemption_reason?: string | null;
  fee_items?: FeeRecordItem[];
  optional_services?: string[];
  late_fee_applied?: number;
  late_fee_days?: number;
  // ── Late fee waiver + one-time settlement ──
  late_fee_exempted?: boolean;
  late_fee_waived?: number;
  is_settled?: boolean;
  settlement_discount?: number;
  settlement_reason?: string | null;
  // ── Overpayment left after an edit (excess money owed back) ──
  refund_amount?: number;
  refund_status?: string | null;
  refund_note?: string | null;
}

interface MonthStatus {
  monthYear: string;
  displayName: string;
  status: 'paid' | 'unpaid' | 'partial' | 'upcoming' | 'exempted';
  record?: FeeRecord;
}

interface StudentFeePanelProps {
  studentId: number | string | null;
  session: string;
  sessionId: string;
  sessionStart: string;
  sessionEnd: string;
  classId?: string | null;
  studentRecord?: {
    student_name: string;
    profile_picture_url?: string | null;
    class: string;
    section: string | null;
    roll_no: number;
  } | null;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2025-26" | "2025-2026" → { startYear, endYear } */
const parseSessionYears = (session: string): { startYear: number; endYear: number } | null => {
  if (!session) return null;
  const parts = session.split('-');
  if (parts.length !== 2) return null;
  const start = parseInt(parts[0], 10);
  let end = parseInt(parts[1], 10);
  if (isNaN(start) || isNaN(end)) return null;
  if (end < 100) end = Math.floor(start / 100) * 100 + end;
  return { startYear: start, endYear: end };
};

const fmtAmount = (v: string | number | null) => {
  if (v === null || v === undefined) return '₹0';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN')}`;
};

const fmtDate = (d: string | null) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtMonthFull = (my: string) => {
  const [year, month] = my.split('-');
  return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
};

const monthYearToMonthName = (my: string): string => {
  const [, month] = my.split('-');
  return MONTH_NAMES[parseInt(month) - 1] ?? '';
};

const getCurrentMonthYear = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const generateSessionMonths = (
  session: string,
  startMonth: string,
  endMonth: string,
): MonthStatus[] => {
  const years = parseSessionYears(session);
  if (!years) return generateFallbackMonths(session);

  const { startYear: yearA, endYear: yearB } = years;
  const startIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === startMonth.toLowerCase());
  const endIdx = MONTH_NAMES.findIndex(m => m.toLowerCase() === endMonth.toLowerCase());

  if (startIdx === -1 || endIdx === -1) {
    return generateFallbackMonths(session);
  }

  const months: MonthStatus[] = [];
  let i = startIdx;
  let wrapped = false;

  while (true) {
    const year = wrapped ? yearB : yearA;
    const mm = String(i + 1).padStart(2, '0');
    const monthYear = `${year}-${mm}`;
    months.push({
      monthYear,
      displayName: `${MONTH_NAMES[i]} ${year}`,
      status: 'upcoming',
    });
    if (i === endIdx) break;
    i = (i + 1) % 12;
    if (i === 0) wrapped = true;
    if (months.length > 24) break;
  }

  return months;
};

const generateFallbackMonths = (session: string): MonthStatus[] => {
  const years = parseSessionYears(session);
  const startYear = years?.startYear ?? new Date().getFullYear();
  const endYear = years?.endYear ?? startYear + 1;
  const order = [
    'November', 'December', 'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August', 'September', 'October',
  ];
  const months: MonthStatus[] = [];
  let year = startYear;
  let monthIdx = 10;
  for (let i = 0; i < 12; i++) {
    const mm = String(monthIdx + 1).padStart(2, '0');
    months.push({
      monthYear: `${year}-${mm}`,
      displayName: `${order[i]} ${year}`,
      status: 'upcoming',
    });
    monthIdx++;
    if (monthIdx > 11) {
      monthIdx = 0;
      year = endYear;
    }
  }
  return months;
};

/**
 * If timeline status is 'upcoming', keep it — don't let API 'unpaid' override future months.
 * A settled month is closed by agreement, so it reads as paid even though
 * paid_amount is below total_amount.
 */
const getEffectiveStatus = (
  status: string,
  record?: FeeRecord,
): 'paid' | 'unpaid' | 'partial' | 'upcoming' | 'exempted' => {
  if (status === 'upcoming') return 'upcoming';
  if (!record) return status as 'paid' | 'unpaid' | 'partial' | 'exempted';
  if (record.fees_status === 'exempted') return 'exempted';
  if (record.is_settled) return 'paid';
  const paid = parseFloat(String(record.paid_amount ?? 0));
  const total = parseFloat(String(record.total_amount ?? 0));
  if (total > 0 && paid >= total) return 'paid';
  if (paid > 0 && paid < total) return 'partial';
  if (paid === 0) return 'unpaid';
  return status as 'paid' | 'unpaid' | 'partial' | 'exempted';
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  paid: { bg: 'rgba(16,185,129,0.1)', text: '#059669', border: 'rgba(16,185,129,0.25)', dot: '#10b981' },
  unpaid: { bg: 'rgba(239,68,68,0.1)', text: '#dc2626', border: 'rgba(239,68,68,0.25)', dot: '#ef4444' },
  partial: { bg: 'rgba(245,158,11,0.1)', text: '#d97706', border: 'rgba(245,158,11,0.25)', dot: '#f59e0b' },
  partially_paid: { bg: 'rgba(245,158,11,0.1)', text: '#d97706', border: 'rgba(245,158,11,0.25)', dot: '#f59e0b' },
  exempted: { bg: 'rgba(99,102,241,0.1)', text: '#4f46e5', border: 'rgba(99,102,241,0.25)', dot: '#6366f1' },
  upcoming: { bg: 'rgba(59,130,246,0.08)', text: '#2563eb', border: 'rgba(59,130,246,0.2)', dot: '#3b82f6' },
};

const STATUS_ICONS: Record<string, string> = {
  paid: 'pi-check-circle',
  unpaid: 'pi-times-circle',
  partial: 'pi-exclamation-circle',
  partially_paid: 'pi-exclamation-circle',
  exempted: 'pi-shield',
  upcoming: 'pi-clock',
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const PANEL_CSS = `
  @keyframes sfp-slideIn  { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes sfp-fadeIn   { from{opacity:0} to{opacity:1} }
  @keyframes sfp-slideUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes sfp-shimmer  { 0%{background-position:-500px 0} 100%{background-position:500px 0} }

  .sfp-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 420px;
    background: #fff;
    box-shadow: -8px 0 48px rgba(30,58,138,0.13);
    display: flex; flex-direction: column;
    z-index: 1000;
    animation: sfp-slideIn 0.28s cubic-bezier(0.4,0,0.2,1);
    font-family: 'DM Sans', system-ui, sans-serif;
  }

  .sfp-scroll::-webkit-scrollbar { width: 4px; }
  .sfp-scroll::-webkit-scrollbar-track { background: transparent; }
  .sfp-scroll::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.12); border-radius: 4px; }

  .sfp-close-btn {
    width: 32px; height: 32px; border-radius: 9px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.25);
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    padding: 0; transition: background 0.15s; flex-shrink: 0;
  }
  .sfp-close-btn:hover { background: rgba(255,255,255,0.28); }

  .sfp-month-row {
    border-radius: 12px;
    border: 1px solid #f0f4f8;
    overflow: hidden;
    background: white;
    margin-bottom: 8px;
    animation: sfp-slideUp 0.2s ease both;
    transition: box-shadow 0.15s, transform 0.13s;
  }
  .sfp-month-row:hover {
    box-shadow: 0 3px 16px rgba(30,58,138,0.09);
    transform: translateY(-1px);
  }

  .sfp-shimmer {
    background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
    background-size: 500px 100%;
    animation: sfp-shimmer 1.3s infinite linear;
    border-radius: 6px;
  }

  .sfp-mark-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 10px; border: none;
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit;
    box-shadow: 0 4px 12px rgba(30,58,138,0.25);
    transition: box-shadow 0.15s, transform 0.13s;
  }
  .sfp-mark-btn:hover {
    box-shadow: 0 6px 18px rgba(30,58,138,0.35);
    transform: translateY(-1px);
  }
  .sfp-mark-btn:disabled {
    opacity: 0.5; cursor: not-allowed; transform: none;
  }

  .sfp-receipt-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 10px;
    border: 1px solid rgba(192,81,10,0.35);
    background: rgba(192,81,10,0.06);
    color: #C0510A; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit;
    transition: background 0.15s, transform 0.13s;
  }
  .sfp-receipt-btn:hover {
    background: rgba(192,81,10,0.12);
    transform: translateY(-1px);
  }

  .sfp-receipt-all-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 10px;
    border: 1px solid rgba(192,81,10,0.3);
    background: transparent;
    color: #C0510A; cursor: pointer; font-size: 11px; font-weight: 700;
    font-family: inherit;
    transition: background 0.15s;
  }
  .sfp-receipt-all-btn:hover { background: rgba(192,81,10,0.07); }

  .sfp-tag {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.5px;
    text-transform: uppercase; padding: 2px 7px; border-radius: 10px;
    margin-left: 7px;
  }
  .sfp-tag.settled { background: rgba(16,185,129,0.12); color: #047857; }
  .sfp-tag.late-waived { background: rgba(148,163,184,0.18); color: #475569; }
  .sfp-tag.refund-due { background: rgba(245,158,11,0.16); color: #b45309; }

  .sfp-refund-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 10px;
    border: 1px solid rgba(245,158,11,0.4);
    background: rgba(245,158,11,0.08);
    color: #b45309; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit; transition: background 0.15s, transform 0.13s;
  }
  .sfp-refund-btn:hover { background: rgba(245,158,11,0.16); transform: translateY(-1px); }

  .sfp-month-card-selectable {
    cursor: pointer;
    transition: all 0.15s;
  }
  .sfp-month-card-selectable:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 16px rgba(30,58,138,0.09);
  }
  .sfp-month-card-selected {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 2px rgba(59,130,246,0.2) !important;
  }
  .sfp-month-checkbox {
    width: 18px; height: 18px;
    border-radius: 6px;
    border: 2px solid #d1d5db;
    background: white;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
    flex-shrink: 0;
  }
  .sfp-month-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }
  .sfp-month-checkbox.checked i {
    color: white;
    font-size: 10px;
  }

  .sfp-toolbar {
    display: flex; align-items: center; gap: 10px;
    padding: 0 0 12px 0;
    flex-wrap: wrap;
    border-bottom: 1px solid #f0f4f8;
  }
  .sfp-toolbar-btn {
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
  }
  .sfp-toolbar-btn:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  .sfp-toolbar-btn.primary {
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
    color: white;
    border: none;
  }
  .sfp-toolbar-btn.primary:hover {
    box-shadow: 0 4px 12px rgba(30,58,138,0.25);
    transform: translateY(-1px);
  }
  .sfp-toolbar-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Avatar
// ─────────────────────────────────────────────────────────────────────────────
const PanelAvatar: React.FC<{ name: string; pictureUrl?: string | null; size?: number }> = ({
  name, pictureUrl, size = 52,
}) => {
  const [err, setErr] = useState(false);
  if (pictureUrl && !err) {
    return (
      <img
        src={pictureUrl}
        alt={name}
        onError={() => setErr(true)}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: '3px solid rgba(255,255,255,0.4)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'rgba(255,255,255,0.2)',
      border: '3px solid rgba(255,255,255,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_COLORS[status] ?? STATUS_COLORS.upcoming;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20,
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <i className={`pi ${STATUS_ICONS[status] ?? 'pi-circle'}`} style={{ fontSize: 10 }} />
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Month card with selectable mode
// ─────────────────────────────────────────────────────────────────────────────
const MonthCard: React.FC<{
  month: MonthStatus;
  index: number;
  isCurrentMonth: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  studentId: number | string;
  session: string;
  onMarkPayment: (monthYear: string, record?: FeeRecord) => void;
  onPrintReceipt: (monthYear: string) => void;
  onDeleteRecord: (monthYear: string, record: FeeRecord) => void;
  onMarkRefunded: (record: FeeRecord) => void;
  onToggleSelect?: (monthYear: string) => void;
}> = ({
  month,
  index,
  isCurrentMonth,
  isSelectable,
  isSelected,
  onMarkPayment,
  onPrintReceipt,
  onDeleteRecord,
  onMarkRefunded,
  onToggleSelect,
}) => {
  const [open, setOpen] = useState(isCurrentMonth);
  const effectiveStatus = getEffectiveStatus(month.status, month.record);
  const sc = STATUS_COLORS[effectiveStatus] ?? STATUS_COLORS.upcoming;
  const isSettled = !!month.record?.is_settled;

  const canMarkPayment =
    (effectiveStatus === 'unpaid' ||
      effectiveStatus === 'partial' ||
      effectiveStatus === 'upcoming') &&
    !isSettled;
  const canEditPaid = effectiveStatus === 'paid' && !!month.record?.fee_record_id;
  const canDelete =
    (effectiveStatus === 'paid' || effectiveStatus === 'partial' || effectiveStatus === 'exempted') &&
    !!month.record?.fee_record_id;
  const canPrintReceipt =
    effectiveStatus === 'paid' || effectiveStatus === 'partial';

  const handleCardClick = (e: React.MouseEvent) => {
    // If selectable, toggle selection
    if (isSelectable && onToggleSelect && canMarkPayment) {
      e.stopPropagation();
      onToggleSelect(month.monthYear);
    } else {
      setOpen(o => !o);
    }
  };

  const isCheckboxVisible = isSelectable && canMarkPayment;

  return (
    <div
      className={`sfp-month-row ${isSelectable ? 'sfp-month-card-selectable' : ''} ${
        isSelected && isCheckboxVisible ? 'sfp-month-card-selected' : ''
      }`}
      style={{
        animationDelay: `${index * 0.03}s`,
        border: isCurrentMonth ? `1.5px solid ${sc.border}` : undefined,
      }}
      onClick={handleCardClick}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg, ${sc.dot}, ${sc.dot}88)` }} />

      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isSelectable ? 'pointer' : 'default',
          background: isCurrentMonth ? sc.bg : 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {isCheckboxVisible && (
            <div
              className={`sfp-month-checkbox ${isSelected ? 'checked' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onToggleSelect) onToggleSelect(month.monthYear);
              }}
            >
              {isSelected && <i className="pi pi-check" />}
            </div>
          )}
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              flexShrink: 0,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className={`pi ${STATUS_ICONS[effectiveStatus] ?? 'pi-circle'}`} style={{ fontSize: 15, color: sc.text }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
              {fmtMonthFull(month.monthYear)}
              {isCurrentMonth && (
                <span style={{
                  marginLeft: 7, fontSize: 9, fontWeight: 800,
                  background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
                  color: 'white', padding: '2px 7px', borderRadius: 10,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  Current
                </span>
              )}
              {isSettled && <span className="sfp-tag settled">Settled</span>}
              {month.record?.late_fee_exempted && (
                <span className="sfp-tag late-waived">Late fee waived</span>
              )}
              {(month.record?.refund_amount ?? 0) > 0 &&
                month.record?.refund_status === 'pending' && (
                  <span className="sfp-tag refund-due">Refund due</span>
                )}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              {month.record?.receipt_no
                ? `Receipt: ${month.record.receipt_no}`
                : effectiveStatus === 'upcoming'
                  ? 'Upcoming month'
                  : effectiveStatus === 'unpaid'
                    ? 'No payment recorded'
                    : effectiveStatus === 'exempted'
                      ? 'Exempted'
                      : 'Partial payment'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusBadge status={effectiveStatus} />
          {!isSelectable && (
            <i
              className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'}`}
              style={{ fontSize: 11, color: '#9ca3af' }}
            />
          )}
        </div>
      </div>

      {open && !isSelectable && (
        <div style={{
          padding: '0 16px 16px',
          borderTop: '1px solid #f3f4f6',
          animation: 'sfp-fadeIn 0.18s ease',
        }}>
          {month.record && effectiveStatus !== 'upcoming' ? (
            <div style={{ paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { label: 'Total Amount', val: fmtAmount(month.record.total_amount), color: '#1f2937' },
                  {
                    label: 'Paid Amount',
                    val: fmtAmount(month.record.paid_amount),
                    color: effectiveStatus === 'paid' ? '#059669' : '#dc2626',
                  },
                ].map(({ label, val, color }) => (
                  <div
                    key={label}
                    style={{
                      flex: 1, background: '#f8fafc', borderRadius: 10,
                      padding: '10px 12px', textAlign: 'center', border: '1px solid #f0f4f8',
                    }}
                  >
                    <div style={{
                      fontSize: 10, color: '#9ca3af', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4,
                    }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
                  </div>
                ))}
              </div>

              {(
                [
                  month.record.paid_date && {
                    label: 'Paid On',
                    val: fmtDate(month.record.paid_date)!,
                    icon: 'pi-calendar-check',
                  },
                  month.record.pay_mode && {
                    label: 'Payment Mode',
                    val: month.record.pay_mode.toUpperCase(),
                    icon: 'pi-credit-card',
                  },
                  month.record.receipt_no && {
                    label: 'Receipt No',
                    val: month.record.receipt_no,
                    icon: 'pi-file',
                  },
                  (month.record.late_fee_applied ?? 0) > 0 && {
                    label: 'Late Fee',
                    val: `${fmtAmount(month.record.late_fee_applied ?? 0)}${
                      month.record.late_fee_days ? ` (${month.record.late_fee_days}d late)` : ''
                    }`,
                    icon: 'pi-clock',
                  },
                  month.record.late_fee_exempted && {
                    label: 'Late Fee Waived',
                    val: fmtAmount(month.record.late_fee_waived ?? 0),
                    icon: 'pi-ban',
                  },
                  (month.record.refund_amount ?? 0) > 0 && {
                    label: month.record.refund_status === 'refunded' ? 'Refunded' : 'Refund Due',
                    val: `${fmtAmount(month.record.refund_amount ?? 0)}${
                      month.record.refund_note ? ` · ${month.record.refund_note}` : ''
                    }`,
                    icon: 'pi-replay',
                  },
                  month.record.is_settled && {
                    label: 'Settlement',
                    val: `${fmtAmount(month.record.settlement_discount ?? 0)} waived${
                      month.record.settlement_reason ? ` · ${month.record.settlement_reason}` : ''
                    }`,
                    icon: 'pi-check-square',
                  },
                ] as ({ label: string; val: string; icon: string } | false)[]
              )
                .filter(Boolean)
                .map(item => {
                  const i = item as { label: string; val: string; icon: string };
                  return (
                    <div
                      key={i.label}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', borderRadius: 8,
                        background: '#f8fafc', border: '1px solid #f0f4f8',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <i className={`pi ${i.icon}`} style={{ fontSize: 13, color: '#9ca3af' }} />
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{i.label}</span>
                      </div>
                      <span style={{ fontSize: 12, color: '#1f2937', fontWeight: 700, textAlign: 'right' }}>
                        {i.val}
                      </span>
                    </div>
                  );
                })}

              <div style={{ display: 'flex', gap: 8 }}>
                {canMarkPayment && (
                  <button
                    className="sfp-mark-btn"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={e => {
                      e.stopPropagation();
                      onMarkPayment(month.monthYear, month.record);
                    }}
                  >
                    <i className="pi pi-indian-rupee" style={{ fontSize: 12 }} />
                    {effectiveStatus === 'partial' ? 'Edit / Complete Payment' : 'Mark as Paid'}
                  </button>
                )}
                {canEditPaid && (
                  <button
                    className="sfp-mark-btn"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={e => {
                      e.stopPropagation();
                      onMarkPayment(month.monthYear, month.record);
                    }}
                  >
                    <i className="pi pi-pencil" style={{ fontSize: 12 }} />
                    {isSettled ? 'Edit Settlement' : 'Edit Fee Record'}
                  </button>
                )}
                {canPrintReceipt && (
                  <button
                    className="sfp-receipt-btn"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={e => {
                      e.stopPropagation();
                      onPrintReceipt(month.monthYear);
                    }}
                  >
                    <i className="pi pi-file-pdf" style={{ fontSize: 12 }} />
                    Print Receipt
                  </button>
                )}
                {(month.record.refund_amount ?? 0) > 0 &&
                  month.record.refund_status === 'pending' && (
                    <button
                      className="sfp-refund-btn"
                      style={{ flex: 1, justifyContent: 'center' }}
                      onClick={e => {
                        e.stopPropagation();
                        onMarkRefunded(month.record!);
                      }}
                    >
                      <i className="pi pi-replay" style={{ fontSize: 12 }} />
                      Mark {fmtAmount(month.record.refund_amount ?? 0)} Refunded
                    </button>
                  )}
                {canDelete && (
                  <button
                    className="sfp-receipt-btn"
                    style={{ flex: '0 0 auto', justifyContent: 'center', color: '#dc2626', borderColor: '#fecaca' }}
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteRecord(month.monthYear, month.record!);
                    }}
                    title="Delete this fee record"
                  >
                    <i className="pi pi-trash" style={{ fontSize: 12 }} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ paddingTop: 14 }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 10, marginBottom: canMarkPayment ? 12 : 0,
                  background:
                    effectiveStatus === 'upcoming'
                      ? 'rgba(59,130,246,0.04)'
                      : 'rgba(239,68,68,0.04)',
                  border: `1px solid ${
                    effectiveStatus === 'upcoming'
                      ? 'rgba(59,130,246,0.15)'
                      : 'rgba(239,68,68,0.12)'
                  }`,
                }}
              >
                <i
                  className={`pi ${
                    effectiveStatus === 'upcoming' ? 'pi-clock' : 'pi-info-circle'
                  }`}
                  style={{
                    fontSize: 17,
                    color: effectiveStatus === 'upcoming' ? '#3b82f6' : '#ef4444',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, lineHeight: 1.5 }}>
                  {effectiveStatus === 'upcoming'
                    ? 'This month is upcoming — you can still record an advance payment.'
                    : 'No payment has been recorded for this month.'}
                </span>
              </div>

              {canMarkPayment && (
                <button
                  className="sfp-mark-btn"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={e => {
                    e.stopPropagation();
                    onMarkPayment(month.monthYear);
                  }}
                >
                  <i className="pi pi-indian-rupee" style={{ fontSize: 12 }} />
                  {effectiveStatus === 'upcoming' ? 'Record Advance Payment' : 'Mark as Paid'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────
const StudentFeePanel: React.FC<StudentFeePanelProps> = ({
  studentId,
  session,
  sessionId,
  sessionStart,
  sessionEnd,
  classId,
  studentRecord,
  onClose,
  onPaymentSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const { schoolDetails } = useSchoolDetailsStore();
  const { classOptions } = useClassesStore();

  const schoolId = schoolDetails?.school_id ?? '';
  const currentSession = schoolDetails?.current_session ?? null;
  const effectiveSessionId = sessionId || currentSession?.id || '';
  const effectiveSession = session || currentSession?.session || '';

  // ── Resolve class ID from props or class name ──
  const resolvedClassId = useMemo(() => {
    if (classId) return classId;
    const name = studentRecord?.class?.trim();
    if (!name || !classOptions?.length) return null;
    const found = classOptions.find(
      (c: any) =>
        c.label?.toLowerCase() === name.toLowerCase() ||
        c.value?.toLowerCase() === name.toLowerCase()
    );
    return found?.id ?? null;
  }, [classId, studentRecord?.class, classOptions]);

  const [records, setRecords] = useState<FeeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Multi-select state
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);

  // Mark modal state
  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [markMonthYears, setMarkMonthYears] = useState<string[]>([]);
  const [markExistingRecord, setMarkExistingRecord] = useState<FeeRecord | null>(null);

  // Lump sum modal state
  const [lumpSumModalOpen, setLumpSumModalOpen] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetRecord, setDeleteTargetRecord] = useState<FeeRecord | null>(null);

  // Receipt state
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptMonths, setReceiptMonths] = useState<string[]>([]);

  const apiDateRange = (() => {
    const years = parseSessionYears(effectiveSession);
    if (!years) {
      return { startMonthYear: '', endMonthYear: '' };
    }
    const { startYear: yearA, endYear: yearB } = years;

    const startIdx = MONTH_NAMES.findIndex(
      m => m.toLowerCase() === sessionStart.toLowerCase(),
    );
    const endIdx = MONTH_NAMES.findIndex(
      m => m.toLowerCase() === sessionEnd.toLowerCase(),
    );

    if (startIdx === -1 || endIdx === -1) {
      return { startMonthYear: '', endMonthYear: '' };
    }

    const startMM = String(startIdx + 1).padStart(2, '0');
    const endMM = String(endIdx + 1).padStart(2, '0');

    return {
      startMonthYear: `${yearA}-${startMM}`,
      endMonthYear: startIdx > endIdx ? `${yearB}-${endMM}` : `${yearA}-${endMM}`,
    };
  })();

  /**
   * GET /api/fee-records/monthly
   * Required: student_id, session_id, start_month_year, end_month_year
   */
  const fetchDetails = useCallback(async () => {
    if (!studentId || !effectiveSessionId) return;
    if (!apiDateRange.startMonthYear || !apiDateRange.endMonthYear) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        student_id: String(studentId),
        session_id: effectiveSessionId,
        start_month_year: apiDateRange.startMonthYear,
        end_month_year: apiDateRange.endMonthYear,
      });

      const res = await apiClient.get(
        `/api/fee-records/monthly?${params.toString()}`,
      );

      if (res.data.status === 'success') {
        const raw = res.data.data || [];
        const mapped: FeeRecord[] = raw.map((r: any) => {
          let feeRecordId: string | null = null;
          if (r.fee_record_id != null) {
            feeRecordId =
              typeof r.fee_record_id === 'string'
                ? r.fee_record_id
                : r.fee_record_id?.$oid
                  ? r.fee_record_id.$oid
                  : String(r.fee_record_id);
          }

          let status = (r.fees_status || 'unpaid') as FeeRecord['fees_status'];
          if (status === 'partially_paid') status = 'partial';

          return {
            student_id: String(studentId),
            student_name: studentRecord?.student_name ?? 'Student',
            class: studentRecord?.class ?? '',
            section: studentRecord?.section ?? null,
            roll_no: studentRecord?.roll_no ?? 0,
            fee_record_id: feeRecordId,
            session: effectiveSession,
            month_year: r.month_year,
            receipt_no: r.receipt_no ?? null,
            paid_date: r.paid_date ?? null,
            total_amount: r.total_amount ?? null,
            paid_amount: r.paid_amount ?? null,
            fees_status: status,
            pay_mode: r.pay_mode ?? null,
            profile_picture_url: studentRecord?.profile_picture_url ?? null,
            // ── Full breakdown, passed straight through to the receipt ──
            is_exempted: r.is_exempted ?? false,
            exemption_reason: r.exemption_reason ?? null,
            fee_items: r.fee_items ?? [],
            optional_services: r.optional_services ?? [],
            late_fee_applied: r.late_fee_applied ?? 0,
            late_fee_days: r.late_fee_days ?? 0,
            // ── Late fee waiver + settlement ──
            late_fee_exempted: r.late_fee_exempted ?? false,
            late_fee_waived: r.late_fee_waived ?? 0,
            is_settled: r.is_settled ?? false,
            settlement_discount: r.settlement_discount ?? 0,
            settlement_reason: r.settlement_reason ?? null,
            refund_amount: r.refund_amount ?? 0,
            refund_status: r.refund_status ?? null,
            refund_note: r.refund_note ?? null,
          };
        });
        setRecords(mapped);
      } else {
        setRecords([]);
        toast.current?.show({
          severity: 'warn',
          summary: 'Fees',
          detail: res.data.message || 'Could not load fee records',
          life: 3500,
        });
      }
    } catch (e: any) {
      console.error('Failed to load student fees', e);
      setRecords([]);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: e.response?.data?.message || 'Failed to load student fees',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    studentId,
    effectiveSessionId,
    effectiveSession,
    apiDateRange.startMonthYear,
    apiDateRange.endMonthYear,
    studentRecord,
  ]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // Build timeline
  const timeline: MonthStatus[] = (() => {
    const sessionMonths = generateSessionMonths(
      effectiveSession,
      sessionStart,
      sessionEnd,
    );
    const map = new Map<string, FeeRecord>();
    records.forEach(r => map.set(r.month_year, r));
    const now = new Date();

    return sessionMonths.map(m => {
      const rec = map.get(m.monthYear);
      const [y, mo] = m.monthYear.split('-').map(Number);
      const isUpcoming = new Date(y, mo - 1) > now;

      if (isUpcoming) {
        return { ...m, status: 'upcoming' as const, record: rec };
      }
      if (rec) {
        const status =
          rec.fees_status === 'partially_paid' ? 'partial' : rec.fees_status;
        return { ...m, status: status as MonthStatus['status'], record: rec };
      }
      return { ...m, status: 'unpaid' as const };
    });
  })();

  // Check if a month is markable (can be selected for bulk payment).
  // Settled months are closed by agreement and stay out of bulk selection.
  const isMarkable = (m: MonthStatus) => {
    if (m.record?.is_settled) return false;
    const es = getEffectiveStatus(m.status, m.record);
    return es === 'unpaid' || es === 'partial' || es === 'upcoming';
  };

  // Toggle individual month selection
  const toggleMonth = (monthYear: string) => {
    setSelectedMonths((prev) =>
      prev.includes(monthYear) ? prev.filter((x) => x !== monthYear) : [...prev, monthYear]
    );
  };

  // Select all markable months
  const selectAllMarkable = () => {
    setSelectedMonths(timeline.filter(isMarkable).map((m) => m.monthYear));
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedMonths([]);
    setIsSelectMode(false);
  };

  // Handle marking a single payment
  const handleMarkPayment = (monthYear: string, record?: FeeRecord) => {
    setMarkMonthYears([monthYear]);
    setMarkExistingRecord(record && record.fee_record_id != null ? record : null);
    setMarkModalOpen(true);
  };

  // Handle deleting an existing fee record
  const handleDeleteRecord = (_monthYear: string, record: FeeRecord) => {
    setDeleteTargetRecord(record);
    setDeleteModalOpen(true);
  };

  // Handle marking selected months
  const handleMarkSelected = () => {
    if (selectedMonths.length === 0) return;
    setMarkMonthYears([...selectedMonths].sort());
    setMarkExistingRecord(null);
    setMarkModalOpen(true);
  };

  // Close out a pending refund once the money has actually been handed back.
  const handleMarkRefunded = async (record: FeeRecord) => {
    if (!record.fee_record_id) return;
    try {
      const res = await apiClient.patch(`/api/fee-records/${record.fee_record_id}/refund`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Refund closed',
          detail: res.data.message || 'Refund marked as returned.',
          life: 3000,
        });
        fetchDetails();
        onPaymentSuccess?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: res.data.message || 'Could not update the refund',
          life: 5000,
        });
      }
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: e.response?.data?.message || 'Could not update the refund',
        life: 5000,
      });
    }
  };

  const handlePrintReceipt = (monthYear: string) => {
    setReceiptMonths([monthYearToMonthName(monthYear)]);
    setReceiptOpen(true);
  };

  const handlePrintAllReceipts = () => {
    const paidMonthNames = timeline
      .filter(m => {
        const es = getEffectiveStatus(m.status, m.record);
        return es === 'paid' || es === 'partial';
      })
      .map(m => monthYearToMonthName(m.monthYear))
      .filter(Boolean);
    if (paidMonthNames.length === 0) return;
    setReceiptMonths(paidMonthNames);
    setReceiptOpen(true);
  };

  const handlePaymentSuccess = () => {
    setMarkModalOpen(false);
    setMarkMonthYears([]);
    setMarkExistingRecord(null);
    setSelectedMonths([]);
    setIsSelectMode(false);
    fetchDetails();
    onPaymentSuccess?.();
  };

  const handleLumpSumSuccess = () => {
    setSelectedMonths([]);
    setIsSelectMode(false);
    fetchDetails();
    onPaymentSuccess?.();
  };

  // Stats — only past/current months. A settled month owes nothing further,
  // so its shortfall never lands in "Due".
  const stats = records.reduce(
    (acc, r) => {
      const [y, mo] = r.month_year.split('-').map(Number);
      const isUpcoming = new Date(y, mo - 1) > new Date();
      if (isUpcoming) return acc;

      const paid = parseFloat(String(r.paid_amount ?? 0));
      const total = parseFloat(String(r.total_amount ?? 0));
      if (r.fees_status === 'paid' || r.is_settled) acc.paid += paid;
      if (
        !r.is_settled &&
        (r.fees_status === 'unpaid' || r.fees_status === 'partial') &&
        total > 0
      ) {
        acc.unpaid += total - paid;
      }
      const key = r.is_settled
        ? 'paid'
        : r.fees_status === 'partially_paid'
          ? 'partial'
          : r.fees_status;
      acc.months[key] = (acc.months[key] || 0) + 1;
      return acc;
    },
    { paid: 0, unpaid: 0, months: {} as Record<string, number> },
  );

  const currentMonthYear = getCurrentMonthYear();
  const studentName =
    studentRecord?.student_name ?? records[0]?.student_name ?? 'Student';
  const studentClass = studentRecord?.class ?? records[0]?.class ?? '';
  const studentSection = studentRecord?.section ?? records[0]?.section ?? null;
  const studentRoll = studentRecord?.roll_no ?? records[0]?.roll_no ?? '';

  const paidMonthsCount = timeline.filter(m => {
    const es = getEffectiveStatus(m.status, m.record);
    return es === 'paid' || es === 'partial';
  }).length;

  const markableCount = timeline.filter(isMarkable).length;
  const selectedCount = selectedMonths.length;

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{PANEL_CSS}</style>

      <div className="sfp-panel">
        {/* ── Header ── */}
        <div
          style={{
            flexShrink: 0,
            padding: '20px 20px 18px',
            background:
              'linear-gradient(135deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 18,
            }}
          >
            <PanelAvatar
              name={studentName}
              pictureUrl={studentRecord?.profile_picture_url}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: 'white',
                  lineHeight: 1.2,
                  marginBottom: 6,
                }}
              >
                {studentName}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  {
                    icon: 'pi-book',
                    label: `Class ${studentClass}${
                      studentSection ? ` · ${studentSection}` : ''
                    }`,
                  },
                  { icon: 'pi-id-card', label: `Roll ${studentRoll}` },
                  { icon: 'pi-calendar', label: effectiveSession },
                ].map(({ icon, label }) => (
                  <span
                    key={label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.75)',
                      background: 'rgba(255,255,255,0.12)',
                      padding: '3px 9px',
                      borderRadius: 20,
                      border: '1px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    <i className={`pi ${icon}`} style={{ fontSize: 10 }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <button className="sfp-close-btn" onClick={onClose}>
              <i className="pi pi-times" style={{ fontSize: 12, color: 'white' }} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {[
              {
                label: 'Paid',
                val: fmtAmount(stats.paid),
                color: '#34d399',
                bg: 'rgba(52,211,153,0.15)',
                border: 'rgba(52,211,153,0.3)',
              },
              {
                label: 'Due',
                val: fmtAmount(stats.unpaid),
                color: '#f87171',
                bg: 'rgba(248,113,113,0.15)',
                border: 'rgba(248,113,113,0.3)',
              },
              {
                label: 'Months Paid',
                val: String(stats.months['paid'] ?? 0),
                color: 'rgba(255,255,255,0.9)',
                bg: 'rgba(255,255,255,0.1)',
                border: 'rgba(255,255,255,0.2)',
              },
            ].map(({ label, val, color, bg, border }) => (
              <div
                key={label}
                style={{
                  flex: 1,
                  borderRadius: 10,
                  padding: '9px 10px',
                  background: bg,
                  border: `1px solid ${border}`,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color,
                    marginBottom: 2,
                  }}
                >
                  {val}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div
          style={{
            flexShrink: 0,
            padding: '10px 16px 12px',
            borderBottom: '1px solid #f0f4f8',
            background: '#fafbfc',
          }}
        >
          <div className="sfp-toolbar">
            {!isSelectMode ? (
              <>
                <button
                  className="sfp-toolbar-btn primary"
                  onClick={() => setIsSelectMode(true)}
                  disabled={markableCount === 0}
                >
                  <i className="pi pi-check-square" style={{ marginRight: 5, fontSize: 12 }} />
                  Bulk Mark Payment
                </button>
                <button
                  className="sfp-toolbar-btn"
                  onClick={handlePrintAllReceipts}
                  disabled={paidMonthsCount === 0}
                >
                  <i className="pi pi-file-pdf" style={{ marginRight: 5, fontSize: 12 }} />
                  Print All Receipts ({paidMonthsCount})
                </button>
                <button
                  className="sfp-toolbar-btn"
                  onClick={() => setLumpSumModalOpen(true)}
                >
                  <i className="pi pi-wallet" style={{ marginRight: 5, fontSize: 12 }} />
                  Pay Lump Sum
                </button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {selectedCount} selected
                </span>
                <button
                  className="sfp-toolbar-btn"
                  onClick={selectAllMarkable}
                >
                  Select All ({markableCount})
                </button>
                <button
                  className="sfp-toolbar-btn primary"
                  onClick={handleMarkSelected}
                  disabled={selectedCount === 0}
                >
                  <i className="pi pi-indian-rupee" style={{ marginRight: 5, fontSize: 12 }} />
                  Mark Selected ({selectedCount})
                </button>
                <button
                  className="sfp-toolbar-btn"
                  onClick={clearSelection}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {(['paid', 'unpaid', 'partial', 'upcoming'] as const).map(s => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: STATUS_COLORS[s].dot,
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: '#6b7280',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {s}
                </span>
              </div>
            ))}
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              {timeline.map(m => {
                const es = getEffectiveStatus(m.status, m.record);
                const isCur = m.monthYear === currentMonthYear;
                return (
                  <div
                    key={m.monthYear}
                    title={fmtMonthFull(m.monthYear)}
                    style={{
                      width: isCur ? 10 : 7,
                      height: isCur ? 10 : 7,
                      borderRadius: '50%',
                      background: (STATUS_COLORS[es] ?? STATUS_COLORS.upcoming).dot,
                      border: isCur ? '2px solid #1e3a8a' : 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Month list ── */}
        <div
          className="sfp-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '1px solid #f0f4f8',
                  marginBottom: 8,
                }}
              >
                <div className="sfp-shimmer" style={{ height: 3 }} />
                <div
                  style={{
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    className="sfp-shimmer"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div className="sfp-shimmer" style={{ height: 13, width: '45%' }} />
                    <div className="sfp-shimmer" style={{ height: 10, width: '30%' }} />
                  </div>
                  <div
                    className="sfp-shimmer"
                    style={{ height: 22, width: 60, borderRadius: 20 }}
                  />
                </div>
              </div>
            ))
          ) : (
            timeline.map((month, i) => {
              const isSelected = selectedMonths.includes(month.monthYear);
              const isMarkableMonth = isMarkable(month);
              const selectable = isSelectMode && isMarkableMonth;

              return (
                <MonthCard
                  key={month.monthYear}
                  month={month}
                  index={i}
                  isCurrentMonth={month.monthYear === currentMonthYear}
                  isSelectable={selectable}
                  isSelected={isSelected}
                  studentId={studentId!}
                  session={effectiveSession}
                  onMarkPayment={handleMarkPayment}
                  onPrintReceipt={handlePrintReceipt}
                  onDeleteRecord={handleDeleteRecord}
                  onMarkRefunded={handleMarkRefunded}
                  onToggleSelect={toggleMonth}
                />
              );
            })
          )}
        </div>
      </div>

      {/* MarkFeeModal — supports single, bulk, edit-in-place, and settlement */}
      <MarkFeeModal
        open={markModalOpen}
        onClose={() => {
          setMarkModalOpen(false);
          setMarkMonthYears([]);
          setMarkExistingRecord(null);
        }}
        studentId={studentId != null ? String(studentId) : null}
        session={effectiveSession}
        sessionId={effectiveSessionId}
        monthYear={
          markMonthYears.length === 0
            ? null
            : markMonthYears.length === 1
              ? markMonthYears[0]
              : markMonthYears
        }
        classId={resolvedClassId}
        schoolId={schoolId || undefined}
        sessionStart={sessionStart}
        sessionEnd={sessionEnd}
        studentInfo={{
          student_name: studentName,
          class: studentClass,
          section: studentSection,
          roll_no: typeof studentRoll === 'number' ? studentRoll : Number(studentRoll) || 0,
        }}
        existingRecord={markExistingRecord}
        onSuccess={handlePaymentSuccess}
        toast={toast}
      />

      {/* LumpSumFeeModal */}
      <LumpSumFeeModal
        open={lumpSumModalOpen}
        onClose={() => setLumpSumModalOpen(false)}
        studentId={studentId != null ? String(studentId) : null}
        sessionId={effectiveSessionId}
        session={effectiveSession}
        sessionStart={sessionStart}
        sessionEnd={sessionEnd}
        classId={resolvedClassId}
        schoolId={schoolId || undefined}
        studentInfo={{
          student_name: studentName,
          class: studentClass,
          section: studentSection,
          roll_no: typeof studentRoll === 'number' ? studentRoll : Number(studentRoll) || 0,
        }}
        onSuccess={handleLumpSumSuccess}
        toast={toast}
      />

      {/* DeleteFeeModal — delete a month's fee record from the sidebar */}
      <DeleteFeeModal
        visible={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTargetRecord(null);
        }}
        feeRecordId={deleteTargetRecord?.fee_record_id ?? null}
        feeRecord={deleteTargetRecord}
        onSuccess={() => {
          setDeleteModalOpen(false);
          setDeleteTargetRecord(null);
          fetchDetails();
          onPaymentSuccess?.();
        }}
        toast={toast}
      />

      {receiptOpen && (
        <FeeReceiptGenerator
          studentName={studentName}
          studentClass={`Class ${studentClass}`}
          rollNo={studentRoll}
          session={effectiveSession}
          feeRecords={records}
          preSelectedMonths={receiptMonths}
          onClose={() => setReceiptOpen(false)}
        />
      )}
    </>
  );
};

export default StudentFeePanel;